import React from "react";
import { ChangeEvent, Fragment } from "react";
import ReactModal from "react-modal";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import { DateTime } from "luxon";

import NoteBox from "../NoteBox";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import { V2, apiGet } from "../../Api";
import { formatDateLocal, formatName, formatTelephone, formatDateTimeLocal, formatUSD } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";
import { raceToString } from "../../TextTables";

import "../../blocks.css";
import "../../form.css";
import "../../icomoon.css";
import "../viewer.css";

interface ISurgeryInsuranceViewState {
    isLoading: boolean;
    hasNoteChanged: boolean;
    loadError?: string;

    surgeries: any[];

    surgery?: any;

    hidePostNoteControls: boolean;
    hidePreNoteControls: boolean;
    hideInsuranceNoteControls: boolean;
    allowNoteUploads: boolean;

    isBillingModalOpen: boolean;
    billingNote: string;
    billingBilledAmount: string;
    billingPaidAmount: string;
    billingBilledDeductible: string;
    billingBilledCoinsurance: string;
    billingModalLoading: boolean | string;
    billingModalType: "clinic" | "surgical",

    employees: any[];
}

class SurgeryInsuranceView extends React.Component<RouteComponentProps<any> & IAuthProps, ISurgeryInsuranceViewState> {
    constructor(props: Readonly<RouteComponentProps<any> & IAuthProps>) {
        super(props);

        const state: ISurgeryInsuranceViewState = {
            isLoading: true,
            hasNoteChanged: false,

            surgeries: [],

            hidePostNoteControls: !props.authContext.hasRight("surgery:note:post"),
            hidePreNoteControls: !props.authContext.hasRight("surgery:note:pre"),
            hideInsuranceNoteControls: !props.authContext.hasRight("surgery:note:ins"),
            allowNoteUploads: props.authContext.hasRight("surgery:note:upload"),

            isBillingModalOpen: false,

            employees: [],
            billingNote: "",
            billingBilledAmount: "",
            billingPaidAmount: "",
            billingBilledDeductible: "",
            billingBilledCoinsurance: "",
            billingModalLoading: false,
            billingModalType: "clinic",
        };

        this.state = state;

        this.handlePreNote = this.handlePreNote.bind(this);
        this.handlePostNote = this.handlePostNote.bind(this);
        this.handleInsuranceNote = this.handleInsuranceNote.bind(this);
        this.handlePreopNoteDelete = this.handlePreopNoteDelete.bind(this);
        this.handlePostopNoteDelete = this.handlePostopNoteDelete.bind(this);
        this.handleInsuranceNoteDelete = this.handleInsuranceNoteDelete.bind(this);

        this.dialogInputChanged = this.dialogInputChanged.bind(this);

        this.showBillingDialog = this.showBillingDialog.bind(this);
        this.closeBillingModal = this.closeBillingModal.bind(this);

        this.billingFullyPaid = this.billingFullyPaid.bind(this);
        this.billingPartiallyPaid = this.billingPartiallyPaid.bind(this);
        this.billingNeverPaid = this.billingNeverPaid.bind(this);
        this.billingBilledOut = this.billingBilledOut.bind(this);
    }

    public async componentDidMount() {
        try {
            const surgeries = await V2.get<any>("glossary/surgeries");

            let employees = [];
            if (this.props.authContext.hasRight("surgery:history")) {
                employees = await apiGet<any[]>("employees/summary?limit=400");
            }

            this.setState({
                surgeries,
                employees
            });

            this.loadSurgery();
        }
        catch (e) {
            this.setState({
                loadError: e.message
            });
        }
    }

    public render() {
        if (this.state.loadError) {
            return <div className="viewer-root">
                <div className="error-box">
                    Error loading surgery appointment:<br />
                    <br />
                    {this.state.loadError}
                </div>
            </div>
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-full-root lds-dual-ring-big" />;
        }

        const { billing } = this.state.surgery;

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="right:placeholder">
                            <button type="button" onClick={() => { }}><i className="icon-gears" /><span>Placeholder button</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <ReactModal
                    isOpen={this.state.isBillingModalOpen} onRequestClose={this.closeBillingModal}
                    overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-billing-portal"
                    bodyOpenClassName="modal-billing-open"
                    contentLabel="Update Billing Information">
                    <form className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Update {this.state.billingModalType === "clinic" ? "WCAS" : "Surgeon's Fees"} Billing Information</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-billing-billed" className={"required"}>Amount Billed</label>
                                    <input type="number" id="frm-billing-billed" name="billingBilledAmount" value={this.state.billingBilledAmount} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-billing-paid" className={"required"}>Amount Paid</label>
                                    <input type="number" id="frm-billing-paid" name="billingPaidAmount" value={this.state.billingPaidAmount} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group col-50" />
                                <div className="input-form-group col-25">
                                    <label htmlFor="frm-billing-deductible" className={"required"}>Deductible</label>
                                    <input type="number" id="frm-billing-deductible" name="billingBilledDeductible" value={this.state.billingBilledDeductible} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                                <div className="input-form-group col-25">
                                    <label htmlFor="frm-billing-coinsurance" className={"required"}>Co-Insurance</label>
                                    <input type="number" id="frm-billing-coinsurance" name="billingBilledCoinsurance" value={this.state.billingBilledCoinsurance} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-billing-note">Note <small>(optional)</small></label>
                                    <textarea id="frm-billing-note" name="billingNote" value={this.state.billingNote} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            {
                                this.state.billingModalLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : (typeof this.state.billingModalLoading) === "string"
                                        ? <span className="error-text">{this.state.billingModalLoading}</span>
                                        : ""
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button type="button" className="button" onClick={this.billingBilledOut} disabled={!!this.state.billingPaidAmount}>Billed Out</button>
                                <button type="button" className="button red" onClick={this.billingNeverPaid} disabled={!!this.state.billingPaidAmount}>Never Paid</button>
                                <button type="button" className="button yellow" onClick={this.billingPartiallyPaid} disabled={!this.state.billingPaidAmount || !this.state.billingBilledDeductible}>Partially Paid</button>
                                <button type="button" className="button green" onClick={this.billingFullyPaid} disabled={!this.state.billingPaidAmount || !this.state.billingBilledDeductible}>Fully Paid</button>
                                <button type="button" className="button" onClick={this.closeBillingModal}>Cancel</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Patient Information</h3>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Name</dt>
                                    <dd>{formatName(this.state.surgery.patient.name)}</dd>
                                </div>
                                <div>
                                    <dt>Date of Birth</dt>
                                    <dd>{formatDateLocal(this.state.surgery.patient.birthDate)}</dd>
                                </div>
                            </div>

                            <div className="info-list-row">
                                <div>
                                    <dt>Phone Number</dt>
                                    {
                                        this.state.surgery.patient.phone
                                            ? <dd><a href={`tel:${this.state.surgery.patient.phone}`}>{formatTelephone(this.state.surgery.patient.phone)}</a></dd>
                                            : <dd>N/A</dd>
                                    }
                                </div>
                                <div>
                                    <dt>Email Address</dt>
                                    {
                                        this.state.surgery.patient.email
                                            ? <dd><a href={`mailto:${this.state.surgery.patient.email}`} rel="noopener noreferrer" target="_blank">{this.state.surgery.patient.email}</a></dd>
                                            : <dd>N/A</dd>
                                    }
                                </div>
                            </div>

                            <dt>Address</dt>
                            <dd>{this.state.surgery.patient.address.street}</dd>
                            <dd>{this.state.surgery.patient.address.city}, {this.state.surgery.patient.address.state} {this.state.surgery.patient.address.zip}</dd>

                            <div className="info-list-row">
                                <div>
                                    <dt>Sex</dt>
                                    <dd>{this.sexToString(this.state.surgery.patient.sex)}</dd>
                                </div>
                                <div>
                                    <dt>Race</dt>
                                    <dd>{raceToString(this.state.surgery.patient.race)}</dd>
                                </div>
                            </div>

                            <div className="info-list-row">
                                <div>
                                    <dt>Insurance Provider</dt>
                                    <dd>{this.state.surgery.patient.insuranceProvider}</dd>
                                </div>
                                <div>
                                    <dt>Chart #</dt>
                                    <dd>{this.state.surgery.patient.chartNumber}</dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Case Information</h3>
                        </header>

                        <dl className="info-list">
                            <dt>Location</dt>
                            <dd>{this.state.surgery.location.name}</dd>
                            <dd>
                                <small>
                                    {this.state.surgery.location.address.street} {`${this.state.surgery.location.address.city}, ${this.state.surgery.location.address.state} ${this.state.surgery.location.address.zip}`}
                                </small>
                            </dd>

                            <div className="info-list-row">
                                <div className="col-66">
                                    <dt>Date & Time</dt>
                                    <dd>{this.state.surgery.time ? formatDateTimeLocal(this.state.surgery.timestamp) : formatDateLocal(this.state.surgery.date.substring(0, 10))}</dd>
                                </div>
                                <div className="col-33">
                                    <dt>Status</dt>
                                    <dd>{this.statusToText(this.state.surgery.status)}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div className="col-66">
                                    <dt>Appointment Type</dt>
                                    <dd>{this.appointmentTypeToString(this.state.surgery.appointmentType)}</dd>
                                </div>
                                <div className="col-33">
                                    <dt>Surgery Case</dt>
                                    <dd><Link to={`/surgery/${this.props.match.params.id}`}>View full case</Link></dd>
                                </div>
                            </div>

                            <dt>Primary Operation</dt>
                            <dd>{(this.state.surgery.primarySurgery && this.state.surgery.primarySurgery.name) || "Unknown"}</dd>
                            {
                                this.state.surgery.primarySurgeryOptions.length > 0
                                    ? this.state.surgery.primarySurgeryOptions.map((a: any) => <dd key={a.id}><small>{a.name || a.id}</small></dd>)
                                    : ""
                            }

                            <dt>Additional Operations</dt>
                            {
                                this.state.surgery.additionalSurgeries && this.state.surgery.additionalSurgeries.length > 0
                                    ? this.state.surgery.additionalSurgeries.map((a: any, i: number) => <Fragment key={a.id}>
                                        <dd key={a.id}>{a.surgery.name}</dd>
                                        {
                                            a.options && a.options.length > 0
                                                ? a.options.map((b: any) => <dd key={b.id}><small>{b.name || b.id}</small></dd>)
                                                : ""
                                        }
                                    </Fragment>)
                                    : "None"
                            }
                        </dl>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Insurance Information</h3>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>WCAS Billed</dt>
                                    <dd>{formatUSD(billing.wcasBilled)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Collected</dt>
                                    <dd>{formatUSD(billing.wcasCollected)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Deductible</dt>
                                    <dd>{formatUSD(billing.wcasDeductible)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Co-Insurance</dt>
                                    <dd>{formatUSD(billing.wcasCoinsurance)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Status</dt>
                                    <dd className={this.getColorClassName("cell", billing.wcasStatus)}>{billing ? this.billingStatusToString(billing.wcasStatus) : "-"}</dd>
                                </div>
                                <div className="col-smallest col-button">
                                    <dd>
                                        <button type="button" className="button small" onClick={() => this.showBillingDialog("clinic")}>Edit</button>
                                    </dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Surgeon's fees Billed</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesBilled)}</dd>
                                </div>
                                <div>
                                    <dt>Surgeon's fees Collected</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesCollected)}</dd>
                                </div>
                                <div>
                                    <dt>Surgeon's fees Deductible</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesDeductible)}</dd>
                                </div>
                                <div>
                                    <dt>Surgeon's fees Co-Insurance</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesCoinsurance)}</dd>
                                </div>
                                <div>
                                    <dt>Surgeon's fees Status</dt>
                                    <dd className={this.getColorClassName("cell", billing.surgeonStatus)}>{billing ? this.billingStatusToString(billing.surgeonStatus) : "-"}</dd>
                                </div>
                                <div className="col-smallest col-button">
                                    <dd>
                                        <button type="button" className="button small" onClick={() => this.showBillingDialog("surgical")}>Edit</button>
                                    </dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <div className="row-root  row-root-auto">
                        <section className="info-block">
                            <header>
                                <h3>Pre-Surgery Notes</h3>
                            </header>

                            {this.state.surgery.preSurgeryNotes.length === 0 ? "No notes" : ""}
                            <NoteBox notes={this.state.surgery.preSurgeryNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handlePreNote} hideControls={this.state.hidePreNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handlePreopNoteDelete} uploadFilter="image/jpeg,image/png,application/pdf" allowUpload={this.state.allowNoteUploads} />
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Post-Surgery Notes</h3>
                            </header>

                            {this.state.surgery.postSurgeryNotes.length === 0 ? "No notes" : ""}
                            <NoteBox notes={this.state.surgery.postSurgeryNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handlePostNote} hideControls={this.state.hidePostNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handlePostopNoteDelete} uploadFilter="image/jpeg,image/png,application/pdf" allowUpload={this.state.allowNoteUploads} />
                        </section>
                    </div>
                    <div className="row-root  row-root-auto">
                        <section className="info-block">
                            <header>
                                <h3>Insurance Notes</h3>
                            </header>

                            {!this.state.surgery.insuranceNotes || this.state.surgery.insuranceNotes.length === 0 ? "No notes" : ""}
                            <NoteBox notes={this.state.surgery.insuranceNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handleInsuranceNote} hideControls={this.state.hideInsuranceNoteControls} allowDeletion={this.props.authContext.hasRight("surgery:delete:note")} onNoteDelete={this.handleInsuranceNoteDelete} />
                        </section>
                    </div>
                </div>
            </div>
        );
    }

    private dialogInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const name = e.currentTarget.name;
        const value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;


        if (e.currentTarget.type === "number" && !(e.currentTarget as any).step) {
            this.setState({
                [name]: parseInt(value, 10)
            } as any);
        }
        else {
            this.setState({
                [name]: value
            } as any);
        }
    }

    private async loadSurgery() {
        try {
            this.setState({
                isLoading: true,
            });

            const id = this.props.match.params.id;
            const surgery = await V2.get<any>(`surgery/${id}?mode=insurance`);

            if (surgery) {
                // HACK: Assume time is specified in eastern time for now
                surgery.timestamp = (surgery.time
                    ? DateTime.fromISO(`${surgery.date.substring(0, 10)}T${surgery.time}`, { zone: "America/New_York" })
                    : DateTime.fromISO(`${surgery.date.substring(0, 10)}`, { zone: "America/New_York" })).toLocal();

                // Sort by time
                surgery.preSurgeryNotes && surgery.preSurgeryNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                surgery.postSurgeryNotes && surgery.postSurgeryNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                surgery.insuranceNotes && surgery.insuranceNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));

                if (surgery.preSurgeryNotes) {
                    for (const note of surgery.preSurgeryNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.postSurgeryNotes) {
                    for (const note of surgery.postSurgeryNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.insuranceNotes) {
                    for (const note of surgery.insuranceNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (surgery.primarySurgeryOptions) {
                    for (const opt of surgery.primarySurgeryOptions) {
                        const surgeryType = this.state.surgeries.find(a => a.id === surgery.primarySurgeryId);
                        if (surgeryType) {
                            const surgeryOpt = this.findSurgeryOption(surgeryType.options, opt.id);
                            if (surgeryOpt) {
                                opt.name = surgeryOpt.fullName || surgeryOpt.name;
                            }
                        }
                    }
                }

                if (surgery.additionalSurgeries) {
                    for (const operation of surgery.additionalSurgeries) {
                        for (const opt of operation.options) {
                            const surgeryType = this.state.surgeries.find(a => a.id === operation.id);
                            if (surgeryType) {
                                const surgeryOpt = this.findSurgeryOption(surgeryType.options, opt.id);
                                if (surgeryOpt) {
                                    opt.name = surgeryOpt.fullName || surgeryOpt.name;
                                }
                            }
                        }
                    }
                }

                if (surgery.history) {
                    surgery.history.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));
                    for (const item of surgery.history) {
                        item.author = formatName(item.employee.name);
                        item.body = this.renderHistoryBody(item);
                    }
                }

                if (!surgery.billing) {
                    surgery.billing = {};
                }
            }

            this.setState({
                isLoading: false,
                loadError: undefined,
                surgery,
            });
        }
        catch (e) {
            this.setState({
                isLoading: false,
                loadError: e.message
            });
        }
    }

    private findSurgeryOption(options: any[], id: string): any {
        for (const item of options) {
            if (item.id === id) {
                return item;
            }

            if (item.children) {
                const childFound = this.findSurgeryOption(item.children, id);
                if (childFound) {
                    return childFound;
                }
            }
        }

        return undefined;
    }

    private sexToString(sex: string) {
        switch (sex) {
            case "f":
            case "F": return "Female";
            case "m":
            case "M": return "Male";
            default: return `Unknown (${sex})`;
        }
    }

    private statusToText(status: number) {
        switch (status) {
            case 0: return "Pending";
            case 1: return "Active";
            case 2: return "Canceled";
            case 3: return "Soft-canceled";
            case 4: return "Complete";
            case 5: return "Canceled \u2014 Not needed";
            case 6: return "Failed";
            default: return `Unknown: ${status}`;
        }
    }

    private appointmentTypeToString(type: number) {
        switch (type) {
            case 0: return "OR";
            case 1: return "Office";
            case 2: return "Indigent";
            default: return `Unknown: ${type}`;
        }
    }

    private stringToElements(text: string) {
        if (!text) {
            return null;
        }

        const paragraphs = text.replace(/\r/g, "").split(/\n/g);

        return <Fragment>
            {paragraphs.map((a, i) => <Fragment key={i}>{a}<br /></Fragment>)}
        </Fragment>
    }

    private async handlePreNote(body: string, files?: string[]) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/preop`, { body, files });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.preSurgeryNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handlePostNote(body: string, files?: string[]) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/postop`, { body, files });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.postSurgeryNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handleInsuranceNote(body: string) {
        // Send note to server
        const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/insurance`, { body });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.surgery.insuranceNotes.push(note);

            return {
                hasNoteChanged: false,
                surgery: s.surgery,
            };
        });
    }

    private async handlePreopNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.preSurgeryNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/preop/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.preSurgeryNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private async handlePostopNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.postSurgeryNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/postop/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.postSurgeryNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private async handleInsuranceNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.surgery.insuranceNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await V2.delete(`surgery/${this.state.surgery.id}/notes/insurance/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.surgery.insuranceNotes.splice(index, 1);

            return {
                surgery: s.surgery
            }
        });
    }

    private renderHistoryBody(evt: any) {
        switch (evt.type) {
            case 1: // Created
                return "Appointment created";
            case 2: // Updated
                return "Information updated";
            case 3: // Rescheduled
                return <Fragment>
                    <span>Rescheduled from <b>{formatDateLocal(evt.valueDate)}</b> to <b>{formatDateLocal(evt.valueDate2)}</b></span>
                    <br />
                    <br />
                    <span>
                        Reason:<br />
                        {evt.valueText || "No reason provided"}
                    </span>
                </Fragment>;
            case 4: // Status changed
                return <span>Changed status from <b>{this.statusToText(+evt.valueNumeric)}</b> to <b>{this.statusToText(+evt.valueNumeric2)}</b></span>;
            case 5: // Provider changed
                return <span>Changed provider from <b>{evt.valueNumeric ? formatName(this.lookupEmployee(evt.valueNumeric).name) : "None"}</b> to <b>{formatName(this.lookupEmployee(evt.valueNumeric2).name)}</b></span>;
            case 6: // Marketer changed
                return <span>Changed marketer from <b>{evt.valueNumeric ? formatName(this.lookupEmployee(evt.valueNumeric).name) : "None"}</b> to <b>{formatName(this.lookupEmployee(evt.valueNumeric2).name)}</b></span>;
            case 7: // Operation changed
                return "Operation(s) changed";
            case 8: // WCAS billing changed
                return "Clinic billing values changed";
            case 9: // Surgeon's fees changed
                return "Surgeon's fees values changed";
            default:
                return "Unknown history event type " + evt.type;
        }
    }

    private lookupEmployee(id: string) {
        return this.state.employees.find(a => a.id === id) || {
            name: { first: "Employee ", last: "#" + id }
        };
    }

    private showBillingDialog(kind: "clinic" | "surgical") {
        this.setState({
            billingModalType: kind,
            billingBilledAmount: (kind === "clinic" ? this.state.surgery.billing.wcasBilled : this.state.surgery.billing.surgeonsFeesBilled) || "",
            billingPaidAmount: (kind === "clinic" ? this.state.surgery.billing.wcasCollected : this.state.surgery.billing.surgeonsFeesCollected) || "",
            billingBilledDeductible: (kind === "clinic" ? this.state.surgery.billing.wcasDeductible : this.state.surgery.billing.surgeonsFeesDeductible) || "",
            billingBilledCoinsurance: (kind === "clinic" ? this.state.surgery.billing.wcasCoinsurance : this.state.surgery.billing.surgeonsFeesCoinsurance) || "",
            billingModalLoading: false,
            billingNote: "",

            isBillingModalOpen: true
        });
    }

    private closeBillingModal() {
        this.setState({
            isBillingModalOpen: false
        });
    }

    private async saveBillingInformation(kind: "clinic" | "surgical", status: "billed" | "never" | "partial" | "full") {
        try {
            this.setState({
                billingModalLoading: true,
            });

            const data = {
                status: this.statusToNumber(status),

                billed: this.state.billingBilledAmount,
                collected: this.state.billingPaidAmount,

                deductible: this.state.billingBilledDeductible,
                coinsurance: this.state.billingBilledCoinsurance,

                note: this.state.billingNote,
            };

            await V2.put(`surgery/${this.state.surgery.id}/billing/${kind}`, data);

            this.setState({
                billingModalLoading: false,
                isBillingModalOpen: false,
            }, () => this.loadSurgery());
        }
        catch (e) {
            this.setState({
                billingModalLoading: e.message || e || "Could not save billing info"
            });
        }
    }

    private billingBilledOut() {
        this.saveBillingInformation(this.state.billingModalType, "billed");
    }

    private billingNeverPaid() {
        this.saveBillingInformation(this.state.billingModalType, "never");
    }

    private billingPartiallyPaid() {
        this.saveBillingInformation(this.state.billingModalType, "partial");
    }

    private billingFullyPaid() {
        this.saveBillingInformation(this.state.billingModalType, "full");
    }

    private statusToNumber(status: "billed" | "never" | "partial" | "full") {
        switch (status) {
            case "full": return 3;
            case "partial": return 2;
            case "never": return 1;
            case "billed": return 4;
            default: return 0;
        }
    }

    private billingStatusToString(status?: number) {
        switch (status) {
            case 1: return "Never Paid";
            case 2: return "Partially Paid";
            case 3: return "Fully Paid";
            case 4: return "Billed Out";
            case 0:
            case undefined: return "Not Specified";
            default: return `Unknown (${status})`;
        }
    }

    private getColorClassName(kind: "row" | "cell", status?: number) {
        if (!status)
            return "";

        if (status === 1) {
            return `${kind}-red`;
        }
        if (status === 3) {
            return `${kind}-green`;
        }
        if (status >= 4) {
            return "";
        }
        if (status >= 2) {
            return `${kind}-yellow`;
        }

        return "";
    }
}

export default withTitle("Surgery Insurance Details", withAuth(SurgeryInsuranceView));
